package com.bikompanion.ui.home

import dev.zacsweers.metro.Assisted
import dev.zacsweers.metro.AssistedFactory
import dev.zacsweers.metro.AssistedInject
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.bikompanion.domain.ParkingCountdownRepository
import com.bikompanion.ui.models.ParkingCountdownStatus
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds
import kotlin.time.TimeMark
import kotlin.time.TimeSource

/**
 * Bicing keeps a just-parked bike locked to its dock for this long before it can be picked up
 * again — by anyone, including the rider who just left it. Voting only happens once a bike is
 * already parked, so a recorded vote on a Bicing bike is also the moment this wait starts.
 */
private val BicingPickupDelay = 10.minutes

private val OneSecond = 1.seconds

/** The wait for a just-parked Bicing bike, published as whole seconds left. */
@AssistedInject
class ParkingCountdown(
    @Assisted private val session: HomeSession,
    private val repository: ParkingCountdownRepository,
) {
    @AssistedFactory
    fun interface Factory {
        fun create(session: HomeSession): ParkingCountdown
    }

    /** Counts down [BicingPickupDelay]; a fresh recorded Bicing vote restarts it from ten minutes rather than stacking with whatever was left. */
    private var job: Job? = null

    /**
     * Starts (or restarts) the ten-minute wait for a bike that was just voted on.
     *
     * Restarting rather than resuming: a second Bicing bike voted on while one wait is still
     * running means the rider parked another, and it's that most recent one whose dock they'd
     * be walking back to, so a fresh ten minutes is the answer that matches what they'd see.
     */
    fun start() {
        repository.setDeadline(Clock.System.now().toEpochMilliseconds() + BicingPickupDelay.inWholeMilliseconds)
        run(BicingPickupDelay)
    }

    /**
     * Picks a wait already running back up after the app was closed and reopened.
     *
     * The stored deadline is wall-clock time, since that's the only clock that means anything
     * across a process boundary — a [TimeMark] taken by the process that's gone doesn't. It's
     * converted back to a plain [Duration] immediately, though, so the loop itself only ever
     * has to deal with one notion of time; see [countDownTo].
     */
    fun resume() {
        val deadlineEpochMillis = repository.getDeadline() ?: return
        val remaining = (deadlineEpochMillis - Clock.System.now().toEpochMilliseconds()).milliseconds
        if (remaining <= Duration.ZERO) {
            repository.setDeadline(null)
            return
        }
        run(remaining)
    }

    private fun run(duration: Duration) {
        job?.cancel()
        val deadline = TimeSource.Monotonic.markNow() + duration
        job = session.scope.launch { countDownTo(deadline) }
    }

    /**
     * Waits for a fixed deadline, publishing whole seconds left.
     *
     * A [TimeMark] rather than wall-clock time, deliberately: it is immune to the clock being
     * set, so a wait cannot be shortened or stretched by the phone picking up a new time from
     * the network mid-countdown. The deadline that survives a restart is stored as wall-clock
     * time instead, because that is the only clock that means anything across a process — see
     * [resume].
     *
     * Under a virtual-time test dispatcher the two clocks come apart: [delay] advances the
     * scheduler's virtual time while [TimeMark.elapsedNow] keeps reading the real monotonic
     * one, which barely moves. So this loop never reaches its deadline there, and a test that
     * drives it with `advanceUntilIdle` hangs rather than settling. Tests covering anything
     * that starts a countdown use `runCurrent` and assert the state the loop last published.
     */
    private suspend fun countDownTo(deadline: TimeMark) {
        while (true) {
            val remaining = -deadline.elapsedNow()
            if (remaining <= Duration.ZERO) break
            session.state.update {
                it.copy(parkingCountdownStatus = ParkingCountdownStatus.Counting(remaining.inWholeMilliseconds.ceilToSeconds()))
            }
            delay(minOf(remaining, OneSecond))
        }
        session.state.update { it.copy(parkingCountdownStatus = ParkingCountdownStatus.Idle) }
        repository.setDeadline(null)
    }
}
